import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { requestApiGetConjunctions } from '@/query/conjunctions/useQueryCdm';
import { convertTableData } from '@/components/astroOne/conjunctionTable/body/conjunctionTableUtils';
import { AdvancedSearchParamsType } from '@/types';
import dayjs from 'dayjs';

export const useGlobalConjunctions = (baseDate: dayjs.Dayjs) => {
  const rangeStart = baseDate.utc().startOf('day').subtract(1, 'day');
  const rangeEnd = rangeStart.add(3, 'day');
  const collectionName = `SPACEMAP_CA_${rangeStart.format('YYYYMMMDD').toUpperCase()}T0000_S1`;
  const normalizedDayKey = rangeStart.format('YYYY-MM-DD');

  const {
    data: globalConjunctionsData,
    fetchNextPage,
    hasNextPage,
    isLoading: isGlobalLoading,
    isFetchingNextPage,
  } = useInfiniteQuery<{ conjunctions: any[]; totalCount: number }, Error>({
    initialPageParam: 0,
    queryKey: ['global-conjunctions-for-day', normalizedDayKey],
    queryFn: ({ pageParam = 0 }) => {
      const params = {
        option: 'SIMPLE',
        limit: 100,
        page: pageParam,
        tcaStart: rangeStart.format('YYYY-MM-DDTHH:mm:ss[Z]'),
        tcaEnd: rangeEnd.format('YYYY-MM-DDTHH:mm:ss[Z]'),
      } as AdvancedSearchParamsType;

      return requestApiGetConjunctions(params, collectionName);
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.totalCount > allPages.length * 100) {
        return allPages.length;
      }
      return undefined;
    },
    staleTime: Infinity,
    gcTime: Infinity,
    placeholderData: (previousData) => previousData,
  });

  const globalTableData = useMemo(() => {
    if (!globalConjunctionsData) {
      return [];
    }
    const allConjunctions = globalConjunctionsData.pages.flatMap(
      page => page.conjunctions
    );
    const formattedData = convertTableData({
      conjunctions: allConjunctions,
      totalCount: allConjunctions.length,
      currentCount: allConjunctions.length,
    });
    return formattedData;
  }, [globalConjunctionsData]);

  const filteredConjunctions = useMemo(() => {
    return globalTableData;
  }, [globalTableData]);

  return {
    globalConjunctionsData,
    globalTableData,
    filteredConjunctions,
    isGlobalLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};
